<template>
  <div class="console">
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("console.title") }}</h1>
        <p class="page-head__lede">{{ t("console.lede") }}</p>
      </div>
    </div>

    <div class="notice notice--info" style="margin-bottom: var(--space-4)">
      <div class="notice__body small">{{ t("console.oneInstallation", { url: apiBase }) }}</div>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>
    <div v-else-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>

    <template v-else-if="doc">
      <!--
        The token warning is above the token field, not below it. By the time a
        developer has pasted a credential into a form, telling them where it goes
        is too late to be a choice.
      -->
      <div class="card console__token">
        <label class="field__label" for="console-token">{{ t("console.token") }}</label>
        <input
            id="console-token"
            class="input"
            v-model="token"
            type="password"
            autocomplete="off"
            spellcheck="false"
            :placeholder="t('console.tokenPlaceholder')"
          />
        <p class="field__hint">{{ t("console.tokenHint") }}</p>
      </div>

      <div class="card">
        <label class="field__label" for="console-op">{{ t("console.endpoint") }}</label>
        <select id="console-op" class="select" v-model="selectedId">
            <option v-for="op in ops" :key="op.id" :value="op.id">
              {{ op.method }} {{ op.path }} — {{ op.summary }}
            </option>
          </select>

        <template v-if="selected">
          <p class="field__hint" style="margin-top: var(--space-2)">
            {{ t("console.scopeNote", { scope: selected.scope || "—" }) }}
          </p>

          <div v-if="pathParams.length" class="console__params">
            <h4 class="console__group">{{ t("console.path") }}</h4>
            <div class="field" v-for="p in pathParams" :key="p.name">
              <label class="field__label">{{ p.name }}</label>
              <input class="input input--mono" v-model="pathValues[p.name]" type="text" :placeholder="p.description" />
            </div>
          </div>

          <div v-if="queryParams.length" class="console__params">
            <h4 class="console__group">{{ t("console.query") }}</h4>
            <div class="field" v-for="p in queryParams" :key="p.name">
              <label class="field__label">{{ p.name }}<span v-if="p.required" class="req"> *</span></label>
              <input class="input input--mono" v-model="queryValues[p.name]" type="text" :placeholder="p.description" />
            </div>
          </div>

          <div v-if="hasBody" class="console__params">
            <h4 class="console__group">{{ t("console.body") }}</h4>
            <textarea v-model="body" class="textarea textarea--mono" rows="10" spellcheck="false"></textarea>
            <p v-if="bodyError" class="field__error">{{ t("console.bodyInvalid") }}</p>
          </div>

          <p class="console__url">
            <code>{{ selected.method }} {{ resolvedUrl }}</code>
          </p>

          <div class="console__send">
            <button type="button" class="btn" :disabled="!canSend || sending" @click="send">
              <span v-if="sending" class="spinner" aria-hidden="true"></span>
              {{ sending ? t("console.sending") : t("console.send") }}
            </button>
            <button type="button" class="btn btn--secondary" @click="copyCurl">
              {{ copied ? t("common.copied") : t("console.copyCurl") }}
            </button>
            <span v-if="!token" class="muted small">{{ t("console.needToken") }}</span>
          </div>
        </template>
      </div>

      <div v-if="response" class="card">
        <h4 class="console__group">
          {{ t("console.response") }}
          <span class="console__status" :class="statusClass">{{ response.status }}</span>
          <span class="muted small">{{ response.ms }} ms</span>
        </h4>
        <p v-if="response.requestId" class="muted small">
          {{ t("console.requestId", { id: response.requestId }) }}
        </p>
        <pre class="console__code">{{ response.body }}</pre>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { fetchDocument, operations, deref, exampleFor, installationApiBase } from "../lib/openapi";

/**
 * The API console.
 *
 * IT SENDS REAL REQUESTS, and the page says so twice. There is no sandbox realm
 * in v1 — an app in development runs against real data for the developer's own
 * accounts and their invited testers — so a console that quietly simulated
 * responses would be teaching a contract nobody can rely on. The cost is that a
 * DELETE here deletes, which is why the warning is at the top rather than in a
 * tooltip.
 *
 * THE TOKEN NEVER LEAVES THE TAB. It lives in a `ref`, not in sessionStorage,
 * not in localStorage and not in the URL — reloading the page clears it. That is
 * deliberately less convenient than remembering it: this field takes a
 * credential that can read a customer's fleet, and a portal that persisted it
 * would be storing other people's access tokens on a developer's laptop.
 *
 * The request goes from the BROWSER to the API host, which is why this works at
 * all: the public API reflects the request origin for CORS and takes its token
 * in a header rather than a cookie, so there is nothing to proxy and no
 * credential passing through the portal's own backend.
 */

const { t } = useI18n();
const route = useRoute();

const doc = ref(null);
const loading = ref(true);
const error = ref(null);

const token = ref("");
const selectedId = ref(null);
const pathValues = ref({});
const queryValues = ref({});
const body = ref("");
const sending = ref(false);
const response = ref(null);
const copied = ref(false);

const apiBase = installationApiBase();

const ops = computed(() => (doc.value ? operations(doc.value) : []));

const selected = computed(() => ops.value.find((op) => op.id === selectedId.value) || null);

const pathParams = computed(() =>
  selected.value ? selected.value.parameters.filter((p) => p.in === "path") : []
);

const queryParams = computed(() =>
  selected.value ? selected.value.parameters.filter((p) => p.in === "query") : []
);

const hasBody = computed(
  () =>
    !!selected.value &&
    ["POST", "PUT", "PATCH"].includes(selected.value.method) &&
    !!selected.value.requestBody
);

const bodyError = computed(() => {
  if (!hasBody.value || !body.value.trim()) return null;
  try {
    JSON.parse(body.value);
    return null;
  } catch (e) {
    return "The body is not valid JSON.";
  }
});

const resolvedUrl = computed(() => {
  if (!selected.value) return "";
  let path = selected.value.path;
  for (const p of pathParams.value) {
    // The placeholder is left visible when unfilled, so the URL line shows what
    // is still missing rather than silently producing `/units//trips`.
    const value = (pathValues.value[p.name] || "").trim();
    path = path.replace(`{${p.name}}`, value ? encodeURIComponent(value) : `{${p.name}}`);
  }
  const query = new URLSearchParams();
  for (const p of queryParams.value) {
    const value = (queryValues.value[p.name] || "").trim();
    if (value) query.set(p.name, value);
  }
  const qs = query.toString();
  return `${apiBase}${path}${qs ? `?${qs}` : ""}`;
});

const canSend = computed(
  () =>
    !!selected.value &&
    !!token.value.trim() &&
    !bodyError.value &&
    !resolvedUrl.value.includes("{")
);

const statusClass = computed(() => {
  if (!response.value) return "";
  if (response.value.status >= 200 && response.value.status < 300) return "ok";
  if (response.value.status >= 500) return "server";
  return "client";
});

/** Reset per-operation inputs, and pre-fill the body from the schema. */
watch(selected, (op) => {
  pathValues.value = {};
  queryValues.value = {};
  response.value = null;
  if (!op) {
    body.value = "";
    return;
  }
  for (const p of op.parameters) {
    if (p.in === "query" && p.schema && p.schema.default !== undefined) {
      queryValues.value[p.name] = String(p.schema.default);
    }
  }
  const schema =
    op.requestBody &&
    op.requestBody.content &&
    op.requestBody.content["application/json"] &&
    op.requestBody.content["application/json"].schema;
  body.value = schema ? JSON.stringify(exampleFor(doc.value, schema) || {}, null, 2) : "";
});

function headers() {
  const out = {
    Authorization: `Bearer ${token.value.trim()}`,
    Accept: "application/json"
  };
  if (hasBody.value) out["Content-Type"] = "application/json";
  // Mutating endpoints require an Idempotency-Key. A fresh one per send is the
  // right default here: the console is for exploring, and a stable key would
  // make the second send replay the first's response and look like a bug.
  if (["POST", "PUT", "PATCH", "DELETE"].includes(selected.value.method)) {
    out["Idempotency-Key"] = cryptoRandom();
  }
  return out;
}

function cryptoRandom() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `console-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function send() {
  if (!canSend.value) return;
  sending.value = true;
  response.value = null;
  const startedAt = Date.now();

  try {
    const res = await fetch(resolvedUrl.value, {
      method: selected.value.method,
      headers: headers(),
      body: hasBody.value && body.value.trim() ? body.value : undefined
    });
    const text = await res.text();
    let pretty = text;
    try {
      pretty = JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
      // Not JSON — show it raw rather than an empty box.
    }
    response.value = {
      status: res.status,
      ms: Date.now() - startedAt,
      requestId: res.headers.get("X-Request-Id"),
      body: pretty
    };
  } catch (e) {
    // A network-level failure has no status, and saying "0" would look like a
    // response. CORS failures land here too, which is why the message names it.
    response.value = {
      status: "—",
      ms: Date.now() - startedAt,
      requestId: null,
      body: `The request did not complete: ${e.message}\n\nIf the API host is on another origin, this is usually a CORS or network error rather than a rejection by the API.`
    };
  } finally {
    sending.value = false;
  }
}

/**
 * cURL for the same request — with the token as a shell variable rather than
 * inline, so pasting the command into a terminal, a ticket or a chat does not
 * paste the credential with it.
 */
function copyCurl() {
  const parts = [`curl -X ${selected.value.method} '${resolvedUrl.value}'`];
  parts.push(`  -H 'Authorization: Bearer '"$ZAROKAA_TOKEN"''`);
  if (hasBody.value) {
    parts.push("  -H 'Content-Type: application/json'");
    parts.push(`  -H 'Idempotency-Key: '"$(uuidgen)"''`);
    parts.push(`  -d '${(body.value || "{}").replace(/'/g, "'\\''")}'`);
  }
  const command = parts.join(" \\\n");

  navigator.clipboard
    .writeText(command)
    .then(() => {
      copied.value = true;
      setTimeout(() => {
        copied.value = false;
      }, 1500);
    })
    .catch(() => {
      error.value = "Could not copy to the clipboard.";
    });
}

onMounted(async () => {
  try {
    doc.value = await fetchDocument();
    const wanted = route.query.op;
    selectedId.value =
      (wanted && ops.value.some((op) => op.id === wanted) && wanted) ||
      (ops.value[0] && ops.value[0].id) ||
      null;
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.console__token { margin-bottom: var(--space-4); }
.console__params { margin-top: 0.75rem; }
.console__group { margin: var(--space-4) 0 var(--space-2); font-size: var(--text-sm); color: var(--ink-muted); text-transform: uppercase; letter-spacing: .04em; }
.req { color: var(--red-500); }

.console__url { margin: 0.75rem 0 0; word-break: break-all; }
.console__send { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.75rem; flex-wrap: wrap; }
.console__code {
  background: var(--bg-code);
  padding: 0.6rem;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 28rem;
  overflow-y: auto;
  font-size: 0.82rem;
}
.console__status { border-radius: 8px; padding: 0 0.45rem; color: #fff; font-size: 0.8rem; margin: 0 0.4rem; }
.console__status.ok { background: #27ae60; }
.console__status.client { background: #e67e22; }
.console__status.server { background: #c0392b; }
</style>
